import { useEffect } from 'preact/hooks';
import { Route, Switch, Redirect } from 'wouter-preact';
import { Menu } from 'lucide-preact';
import { useFetch } from '@/lib/useFetch';
import { seedAgentNames } from '@/lib/format';
import { Sidebar } from '@/components/Sidebar';
import { CommandPalette } from '@/components/CommandPalette';
import { ToastStack } from '@/components/ToastStack';
import { sidebarOpen, closeSidebar } from '@/lib/sidebar';
import { Placeholder } from '@/pages/Placeholder';
import { MissionControl } from '@/pages/MissionControl';
import { Memories } from '@/pages/Memories';
import { HiveMind } from '@/pages/HiveMind';
import { Agents } from '@/pages/Agents';
import { Scheduled } from '@/pages/Scheduled';
import { Audit } from '@/pages/Audit';
import { Usage } from '@/pages/Usage';
import { Settings } from '@/pages/Settings';
import { Voices } from '@/pages/Voices';
import { Chat } from '@/pages/Chat';
import { WarRoom } from '@/pages/WarRoom';
import { AgentFiles } from '@/pages/AgentFiles';
import { Bunker } from '@/pages/Bunker';
import { DEFAULT_ROUTE } from '@/lib/routes';
import { TEACHER } from '@/lib/teacher-mode';
import { Struggling } from '@/pages/teaching/Struggling';
import { Home } from '@/pages/teaching/Home';
import { Rail } from '@/components/teaching/Rail';
import { StudentDetail } from '@/pages/teaching/StudentDetail';
import { Overview } from '@/pages/teaching/Overview';
import { Heatmap } from '@/pages/teaching/Heatmap';
import { Misconceptions } from '@/pages/teaching/Misconceptions';
import { Groups } from '@/pages/teaching/Groups';
import { Progress } from '@/pages/teaching/Progress';
import { StudentProgress } from '@/pages/teaching/StudentProgress';
import { Analysis } from '@/pages/teaching/Analysis';
import { Checkpoints } from '@/pages/teaching/Checkpoints';
import { Commitments } from '@/pages/teaching/Commitments';
import { Login } from '@/pages/setup/Login';
import { ForcedPasswordChange } from '@/pages/setup/ChangePassword';
import { Classes, ClassDetail } from '@/pages/setup/Classes';
import { Tests, TestWizard } from '@/pages/setup/Tests';
import { Marks } from '@/pages/setup/Marks';
import { Scans } from '@/pages/setup/Scans';
import { Identity } from '@/pages/setup/Identity';
import { Review } from '@/pages/setup/Review';
import { Guide } from '@/pages/setup/Guide';
import { Moderation } from '@/pages/setup/Moderation';
import { Descriptors } from '@/pages/setup/Descriptors';
import { Judgements } from '@/pages/setup/Judgements';
import { Feedback } from '@/pages/setup/Feedback';
import { Settings as TeacherSettings } from '@/pages/setup/Settings';
import { me, loadMe } from '@/lib/teaching/teacher-api';
import { Picker } from '@/components/teaching/Picker';
import { AgentBriefs } from '@/pages/teaching/AgentBriefs';
import { WorkspaceNotes } from '@/pages/teaching/WorkspaceNotes';
import { AdminBuildLoop } from '@/pages/teaching/AdminBuildLoop';

export function App() {
  return TEACHER ? <TeacherApp /> : <StockApp />;
}

/** Teacher OS: login gate, icon rail, Teaching + Set-up routes only. */
function TeacherApp() {
  useEffect(() => { void loadMe(); }, []);
  const onLogin = location.pathname.startsWith('/login');
  if (me.value === undefined) return <div class="h-[100dvh] bg-[var(--color-bg)]" />;
  if (me.value === null || onLogin) {
    if (!onLogin) { location.replace(`/login?next=${encodeURIComponent(location.pathname + location.search)}`); return null; }
    return <div class="h-[100dvh] overflow-y-auto bg-[var(--color-bg)] text-[var(--color-text)]"><Login /></div>;
  }
  if (me.value.must_change_password) return <div class="h-[100dvh] overflow-y-auto bg-[var(--color-bg)] text-[var(--color-text)]"><ForcedPasswordChange /></div>;
  return (
    <div class="flex h-screen h-[100dvh] bg-[var(--color-bg)] text-[var(--color-text)]">
      <Rail />
      <main class="flex-1 min-w-0 overflow-hidden">
        <Switch>
          <Route path="/week/commitments"><Commitments /></Route>
          <Route path="/week/agents"><AgentBriefs /></Route>
          <Route path="/week/notes"><WorkspaceNotes /></Route>
          <Route path="/admin"><AdminBuildLoop /></Route>
          <Route path="/teaching"><Home /></Route>
          <Route path="/teaching/students"><Struggling /></Route>
          <Route path="/teaching/overview"><Overview /></Route>
          <Route path="/teaching/heatmap"><Heatmap /></Route>
          <Route path="/teaching/misconceptions"><Misconceptions /></Route>
          <Route path="/teaching/groups"><Groups /></Route>
          <Route path="/teaching/a/:assessmentId/student/:id"><StudentDetail /></Route>
          <Route path="/teaching/progress"><Progress /></Route>
          <Route path="/teaching/student/:id/progress"><StudentProgress /></Route>
          <Route path="/teaching/analysis"><Analysis /></Route>
          <Route path="/teaching/checkpoints"><Checkpoints /></Route>
          <Route path="/setup/classes"><Classes /></Route>
          <Route path="/setup/classes/:id"><ClassDetail /></Route>
          <Route path="/setup/tests"><Tests /></Route>
          <Route path="/setup/tests/:id"><TestWizard /></Route>
          <Route path="/setup/marks"><Marks /></Route>
          <Route path="/setup/scans"><Scans /></Route>
          <Route path="/setup/identity"><Identity /></Route>
          <Route path="/setup/review"><Review /></Route>
          <Route path="/setup/guide"><Guide /></Route>
          <Route path="/setup/moderation"><Moderation /></Route>
          <Route path="/setup/descriptors"><Descriptors /></Route>
          <Route path="/setup/judgements"><Judgements /></Route>
          <Route path="/setup/feedback"><Feedback /></Route>
          <Route path="/setup/settings"><TeacherSettings /></Route>
          <Route path="/"><Redirect to="/teaching" /></Route>
          <Route><Placeholder title="Not found" description="This page does not exist." hideRoadmapNote /></Route>
        </Switch>
      </main>
      <Picker />
      <ToastStack />
    </div>
  );
}

function StockApp() {
  const open = sidebarOpen.value;

  // Seed the global agent-name cache once at app init so every page resolves
  // display names correctly (e.g. main -> "Holden", not the capitalized id
  // "Main"). Pages that don't fetch /api/agents themselves — Audit, the Hive
  // Mind graph — previously fell back to the raw id on a cache miss.
  const agentList = useFetch<{ agents: { id: string; name?: string }[] }>('/api/agents');
  useEffect(() => {
    if (agentList.data?.agents) seedAgentNames(agentList.data.agents);
  }, [agentList.data]);

  return (
    <div class="flex h-screen h-[100dvh] bg-[var(--color-bg)] text-[var(--color-text)]">
      {/* Mobile-only hamburger. Hidden on >=md where the sidebar is
       *  always inline. */}
      <button
        type="button"
        onClick={() => { sidebarOpen.value = true; }}
        class={'md:hidden' + ' fixed top-3 left-3 z-50 p-2 rounded-md bg-[var(--color-card)] border border-[var(--color-border)] text-[var(--color-text)] shadow-md'}
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Backdrop when the mobile drawer is open. Tapping it closes. */}
      {open && (
        <div
          class="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={closeSidebar}
        />
      )}

      <Sidebar />
      <main class={'flex-1 min-w-0 overflow-hidden ' + 'pl-12 md:pl-0'}>
        <Switch>
          <Route path="/mission"><MissionControl /></Route>
          <Route path="/scheduled"><Scheduled /></Route>
          <Route path="/agents"><Agents /></Route>
          <Route path="/agents/:id/files"><AgentFiles /></Route>
          <Route path="/chat"><Chat /></Route>
          <Route path="/bunker"><Bunker /></Route>
          <Route path="/memories"><Memories /></Route>
          <Route path="/hive"><HiveMind /></Route>
          <Route path="/usage"><Usage /></Route>
          <Route path="/audit"><Audit /></Route>
          <Route path="/warroom"><WarRoom /></Route>
          <Route path="/voices"><Voices /></Route>
          <Route path="/settings"><Settings /></Route>

          {/* Common alt slugs that used to point at placeholder pages */}
          <Route path="/hive-mind"><Redirect to="/hive" /></Route>
          <Route path="/hivemind"><Redirect to="/hive" /></Route>
          <Route path="/memory"><Redirect to="/memories" /></Route>

          <Route path="/"><Redirect to={DEFAULT_ROUTE} /></Route>
          <Route>
            <Placeholder
              title="Not found"
              description="This page does not exist. Use ⌘K to jump somewhere."
              hideRoadmapNote
            />
          </Route>
        </Switch>
      </main>
      <CommandPalette />
      <ToastStack />
    </div>
  );
}
